import React, { useState } from 'react';
import {
  Box, VStack, HStack, Text, ScrollView,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import SimpleRTE from '@/common/components/forms/SimpleRTE';
import { OralExamQuestionText } from '@/common/components/OralExamQuestionText';
import HtmlText from '@/common/components/HtmlText';
import ContentGroup from '@/common/components/ContentGroup';
import DimRating from './DimRating';

/** Shared outer height for spoken answer and feedback so the two panes read as a pair. */
const TRANSCRIPT_FEEDBACK_PANE_HEIGHT = 280;
/** SimpleRTE wraps the field in p="$2" (8px × 2); subtract so outer chrome matches. */
const SIMPLE_RTE_INNER_PADDING = 16;
const PANE_BODY_FONT_SIZE = 14;

export interface OralExamQuestionCardProps {
  taskId: string;
  questionId: string;
  questionHeaderTitle: string;
  fullQuestionText: string | null;
  transcriptHtml?: string | null;
  oralExamDimensionKeys: string[];
  isReadOnly: boolean;
  isAutograding: boolean;
  isSaving: boolean;
}

/**
 * OralExamQuestionCard
 * ----
 * Per-question text, spoken-answer chapter, feedback, and dimension ratings.
 * Transcript is a scrollable document; feedback is an equal-height form field.
 */
const OralExamQuestionCard: React.FC<OralExamQuestionCardProps> = ({
  taskId,
  questionId,
  questionHeaderTitle,
  fullQuestionText,
  transcriptHtml,
  oralExamDimensionKeys,
  isReadOnly,
  isAutograding,
  isSaving,
}) => {
  const { t } = useTranslation();
  const evalFieldName = `taskEvaluations.${taskId}.subEvaluations.${questionId}.dimGrades`;
  const showChapterPane = typeof transcriptHtml === 'string';
  const [chapterWidth, setChapterWidth] = useState(480);

  return (
    <ContentGroup
      variant="bordered"
      bg="$bgCard"
      accentColor="$brandBase"
      accentWidth={3}
      p="$4"
      mb="$4"
      testID={`oral-exam-question-${questionId}`}
    >
      <Box mb="$4">
        <Text
          testID={`oral-exam-question-header-${questionId}`}
          size="sm"
          color="$textHeading"
          fontWeight="semibold"
        >
          {questionHeaderTitle}
        </Text>
        {!!fullQuestionText && (
          <Box mt="$2">
            <OralExamQuestionText
              question={fullQuestionText}
              size="sm"
              hideTitle
              testID={`oral-exam-question-text-${questionId}`}
            />
          </Box>
        )}
      </Box>

      <HStack
        space="lg"
        alignItems="stretch"
        sx={{
          '@base': { flexDirection: 'column' },
          '@md': { flexDirection: 'row' },
        }}
      >
        {showChapterPane && (
          <VStack flex={1} minWidth={0} w="100%" space="xs">
            <Text
              size="xs"
              color="$textHint"
              fontWeight="medium"
              textTransform="uppercase"
              letterSpacing={0.6}
            >
              {t('lecturer.evalDesk.oralExam.spokenAnswerLabel')}
            </Text>
            <Box
              h={TRANSCRIPT_FEEDBACK_PANE_HEIGHT}
              bg="$bgSubtle"
              borderWidth={1}
              borderColor="$borderLight"
              borderLeftWidth={3}
              borderLeftColor="$borderStrong"
              borderRadius="$md"
              overflow="hidden"
              testID={`oral-exam-question-chapter-${questionId}`}
              onLayout={(event) => {
                const nextWidth = Math.floor(event.nativeEvent.layout.width) - 24;
                if (nextWidth > 0) setChapterWidth(nextWidth);
              }}
            >
              <ScrollView
                style={{ height: TRANSCRIPT_FEEDBACK_PANE_HEIGHT }}
                contentContainerStyle={{ padding: 12, paddingBottom: 20 }}
                showsVerticalScrollIndicator
              >
                {transcriptHtml === '' ? (
                  <Text size="sm" color="$textSubtle">
                    {t('lecturer.evalDesk.oralExam.noAnswerRecorded')}
                  </Text>
                ) : (
                  <HtmlText
                    content={transcriptHtml}
                    width={chapterWidth}
                    fontSize={PANE_BODY_FONT_SIZE}
                    testID={`oral-exam-question-chapter-html-${questionId}`}
                  />
                )}
              </ScrollView>
            </Box>
          </VStack>
        )}

        <VStack flex={1} minWidth={0} w="100%" space="xs">
          <Text
            size="xs"
            color="$textHint"
            fontWeight="medium"
            textTransform="uppercase"
            letterSpacing={0.6}
          >
            {t('lecturer.evalDesk.oralExam.questionFeedbackLabel')}
          </Text>
          <Box h={TRANSCRIPT_FEEDBACK_PANE_HEIGHT}>
            <SimpleRTE
              name={`taskEvaluations.${taskId}.subEvaluations.${questionId}.questionFeedback`}
              placeholder={t('lecturer.evalDesk.oralExam.questionFeedbackLabel')}
              readonly={isReadOnly || isAutograding || isSaving}
              height={TRANSCRIPT_FEEDBACK_PANE_HEIGHT - SIMPLE_RTE_INNER_PADDING}
              bg="$bgCard"
              restingBorderColor="$borderMedium"
              fieldSize="sm"
            />
          </Box>
        </VStack>
      </HStack>

      <HStack
        space="lg"
        mt="$4"
        flexWrap="wrap"
        alignItems="center"
        sx={{ rowGap: 12 }}
      >
        {oralExamDimensionKeys.map((dimension) => (
          <DimRating
            key={dimension}
            dimension={dimension}
            evalFieldName={evalFieldName}
            isAutograding={isAutograding}
            isSaving={isSaving}
            isReadOnly={isReadOnly}
            layout="inline"
          />
        ))}
      </HStack>
    </ContentGroup>
  );
};

export default OralExamQuestionCard;
